/**
 * EntitlementProvider — single source of truth for app access.
 *
 * Wraps the verify-entitlement edge function with React Query.
 * No optimistic state, no polling, no localStorage flags. Just:
 *   status: 'checking' | 'active' | 'inactive' | 'error'
 *   refetch(): re-runs the server check
 *
 * Refetch triggers:
 *   - app focus (React Query refetchOnWindowFocus)
 *   - manual: after a purchase callback, after retry button
 */

import { createContext, useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { withTimeout } from '@/lib/with-timeout';

export type EntitlementStatus = 'checking' | 'active' | 'inactive' | 'error';

export type EntitlementReason =
  | 'admin'
  | 'lifetime'
  | 'revenuecat_active'
  | 'legacy_stripe'
  | 'native_purchase_grace'
  | 'inactive'
  | 'expired'
  | 'receipt_belongs_to_another_user'
  | 'rc_api_error'
  | 'rc_key_missing'
  | 'unauthorized'
  | 'internal_error';

interface EntitlementResponse {
  active: boolean;
  reason: EntitlementReason;
  tier?: string;
  source?: string;
  current_period_end?: string | null;
  provisional?: boolean;
  graceUntil?: string;
}


interface EntitlementContextValue {
  status: EntitlementStatus;
  reason?: EntitlementReason;
  tier?: string;
  source?: string;
  refetch: () => Promise<void>;
}

const EntitlementContext = createContext<EntitlementContextValue | null>(null);

const QUERY_KEY = ['entitlement'] as const;
const VERIFY_TIMEOUT_MS = 8000;

export function EntitlementProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [...QUERY_KEY, user?.id ?? 'anon'],
    enabled: !!user?.id && !authLoading,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
    queryFn: async (): Promise<EntitlementResponse> => {
      const { data, error } = await withTimeout(
        supabase.functions.invoke('verify-entitlement'),
        VERIFY_TIMEOUT_MS,
        'entitlement_provider_verify',
      );
      if (error) throw error;
      return data as EntitlementResponse;
    },
  });

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    await query.refetch();
  }, [queryClient, query]);

  const value = useMemo<EntitlementContextValue>(() => {
    if (authLoading) return { status: 'checking', refetch };
    if (!user) return { status: 'inactive', refetch };
    if (query.isLoading || query.isFetching && !query.data) return { status: 'checking', refetch };
    if (query.isError) return { status: 'error', refetch };
    if (!query.data) return { status: 'checking', refetch };
    return {
      status: query.data.active ? 'active' : 'inactive',
      reason: query.data.reason,
      tier: query.data.tier,
      source: query.data.source,
      refetch,
    };
  }, [authLoading, user, query.isLoading, query.isFetching, query.isError, query.data, refetch]);

  return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
}

export function useEntitlement(): EntitlementContextValue {
  const ctx = useContext(EntitlementContext);
  if (!ctx) throw new Error('useEntitlement must be used within EntitlementProvider');
  return ctx;
}
